import { TrashBaseIcon } from '@tableau/icons/TrashBaseIcon';
import { ButtonType, ButtonWidget, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';

import { OriginCanonicalizer } from '../utils/OriginCanonicalizer';

export const ALLOWED_ORIGINS_LIST_TEST_ID = 'allowed-origins-list';
export const ALLOWED_ORIGINS_ADD_BUTTON_TEST_ID = 'allowed-origins-add-button';
export const ALLOWED_ORIGINS_ROW_TEST_ID_PREFIX = 'allowed-origins-row';
export const ALLOWED_ORIGINS_DELETE_BUTTON_TEST_ID_PREFIX = 'allowed-origins-delete-button';

interface AllowedOriginsListViewProps {
  /** Raw, in-progress origin tokens as the admin has typed them -- not necessarily canonical. */
  readonly origins: string[];
  readonly onChange: (origins: string[]) => void;
  readonly disabled?: boolean;
  /**
   * Optional async gate called with the row's raw token before it is actually removed --
   * e.g. to reverse-lookup which packages currently declare it and let the admin confirm.
   * Resolve `false` to keep the row. Skipped for empty/never-canonical rows.
   */
  readonly onBeforeDelete?: (origin: string) => Promise<boolean>;
}

/**
 * Controlled list editor for the `extensionPackageAllowedOrigins` site setting: one row per
 * origin, add/delete rows, inline canonicalization feedback per row. Purely controlled by
 * `origins`/`onChange` -- unlike IpAllowlistView, this component owns no fetch/save cycle of
 * its own, because it is integrated into the shared siteSettingsV2 page-level Formik form
 * (see AllowedExtensionOriginsSection), which owns the single page-level Save button.
 */
export function AllowedOriginsListView(props: AllowedOriginsListViewProps): React.ReactElement {
  const { origins, onChange, disabled } = props;

  const handleOriginChange = (index: number, value: string) => {
    const next = origins.slice();
    next[index] = value;
    onChange(next);
  };

  const handleDelete = async (index: number) => {
    const { onBeforeDelete } = props;
    if (onBeforeDelete) {
      const proceed = await onBeforeDelete(origins[index]);
      if (!proceed) {
        return;
      }
    }
    const next = origins.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const handleAdd = () => {
    onChange(origins.concat(['']));
  };

  return (
    <div data-tb-test-id={ALLOWED_ORIGINS_LIST_TEST_ID}>
      {origins.map((origin, index) => {
        const isValid = origin.trim() === '' || OriginCanonicalizer.tryCanonicalizeOrigin(origin) !== undefined;
        return (
          <div
            key={index}
            data-tb-test-id={`${ALLOWED_ORIGINS_ROW_TEST_ID_PREFIX}-${index}`}
            style={{ marginBottom: CommonStyles.Sizes.DefaultSpace }}
          >
            <TextFieldWidget
              label=''
              size={45}
              value={origin}
              valid={isValid}
              message={isValid ? '' : Messages.allowedOriginsInvalidOriginMessage()}
              placeholder={Messages.allowedOriginsPlaceholder()}
              onChange={e => handleOriginChange(index, e.target.value)}
              disabled={disabled}
              testId={`${ALLOWED_ORIGINS_ROW_TEST_ID_PREFIX}-${index}-field`}
            />
            <ButtonWidget
              style={{ minWidth: 0, marginLeft: CommonStyles.Sizes.DefaultSpace, verticalAlign: 'middle' }}
              buttonType={ButtonType.Icon}
              testId={`${ALLOWED_ORIGINS_DELETE_BUTTON_TEST_ID_PREFIX}-${index}`}
              density='high'
              onClick={() => handleDelete(index)}
              disabled={disabled}
            >
              <TrashBaseIcon size={18} alt={Messages.allowedOriginsDeleteOrigin()} />
            </ButtonWidget>
          </div>
        );
      })}
      <ButtonWidget
        buttonType={ButtonType.Outline}
        testId={ALLOWED_ORIGINS_ADD_BUTTON_TEST_ID}
        onClick={handleAdd}
        disabled={disabled}
      >
        {Messages.allowedOriginsAddOrigin()}
      </ButtonWidget>
    </div>
  );
}
